import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ActivityUser, getActivityUsers, getUserActivityTimeline, UserActivityRecord } from '../../services/api.service';

const ActivityTimeline: React.FC = () => {
    const { user } = useAuth();
    const canView = Boolean(user?.is_superuser || user?.profile?.canViewUserActivity);
    const [users, setUsers] = useState<ActivityUser[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<number | undefined>(undefined);
    const [records, setRecords] = useState<UserActivityRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!canView) return;
        const loadUsers = async () => {
            try {
                const data = await getActivityUsers();
                setUsers(data);
            } catch (e) {
                setError('Failed to load users.');
            }
        };
        void loadUsers();
    }, [canView]);

    useEffect(() => {
        if (!canView) return;
        const loadTimeline = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await getUserActivityTimeline(selectedUserId, 300);
                setRecords(data.results || []);
            } catch (e) {
                setError('Failed to load activity timeline.');
            } finally {
                setLoading(false);
            }
        };
        void loadTimeline();
    }, [canView, selectedUserId]);

    const grouped = useMemo(() => {
        const out: Record<string, UserActivityRecord[]> = {};
        records.forEach((r) => {
            const key = moment(r.occurredAt).format('YYYY-MM-DD');
            if (!out[key]) out[key] = [];
            out[key].push(r);
        });
        return out;
    }, [records]);

    if (!canView) {
        return (
            <div className='content-page'>
                <div className='content'>
                    <div className='container-fluid mt-3'>
                        <div className='alert alert-warning'>You do not have permission to view activity timelines.</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='content-page'>
            <div className='content'>
                <div className='container-fluid mt-3'>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                        <h4 className='mb-0'>User Activity Timeline</h4>
                        <div className='d-flex align-items-center gap-2'>
                            <label htmlFor='activity-user' className='me-2 mb-0'>User</label>
                            <select
                                id='activity-user'
                                className='form-select form-select-sm'
                                style={{ minWidth: 260 }}
                                value={selectedUserId ?? ''}
                                onChange={(e) => setSelectedUserId(e.target.value ? Number(e.target.value) : undefined)}
                            >
                                <option value=''>All users</option>
                                {users.map((u) => (
                                    <option key={u.id} value={u.id}>{u.name || u.email} ({u.email})</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {error && <div className='alert alert-danger'>{error}</div>}
                    {loading && <div className='alert alert-info'>Loading timeline...</div>}

                    {!loading && records.length === 0 && <div className='alert alert-secondary'>No activity data found.</div>}

                    {!loading && Object.entries(grouped).map(([day, dayRecords]) => (
                        <div key={day} className='card mb-3'>
                            <div className='card-header fw-bold'>{moment(day).format('ddd, MMM D, YYYY')}</div>
                            <div className='card-body py-2'>
                                {dayRecords.map((record) => (
                                    <div key={record.id} className='border-bottom py-2'>
                                        <div className='d-flex justify-content-between'>
                                            <div>
                                                <span className='badge bg-primary me-2'>{record.actionType}</span>
                                                <strong>{record.userName || record.userEmail}</strong>
                                            </div>
                                            <small className='text-muted'>{moment(record.occurredAt).format('HH:mm:ss')}</small>
                                        </div>
                                        <div className='small text-muted mt-1'>
                                            <span className='me-3'>Path: {record.pagePath || '-'}</span>
                                            <span className='me-3'>Target: {record.targetType || '-'} {record.targetLabel ? `(${record.targetLabel})` : ''}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ActivityTimeline;
